// Keyboard shortcuts for the practice dock: single keys, so they can be hit
// with a hand off the guitar. They're ignored while typing in a field, with
// Ctrl, Alt or Cmd held, while one of the site's dialogs is open, or when
// something else already handled the key (the dock's drag handle moves with
// the arrow keys).
//
// Holding ⌘ (Ctrl off a Mac) shows each key on the button it presses.

import React, { useEffect, useState } from 'react';

// Opening (and closing) each tool's panel.
export const TOOL_KEYS = {
    tuner: 't',
    metronome: 'm',
    'backing-track': 'b',
    chords: 'c',
    changes: 'x',
    log: 'l',
};

export const HELP_KEY = '?';
export const STOP_KEY = 's';
// Hear the chord coming up; the drone on the song's key.
export const NEXT_KEY = 'n';
export const DRONE_KEY = 'd';

// "1"–"9" as a number, else null.
export const chordNumber = key => (/^[1-9]$/.test(key) ? Number(key) : null);

const isField = el => !!el && (el.isContentEditable || /^(input|select|textarea)$/i.test(el.tagName));

// Focus the player moved there with the keyboard (rather than a click).
const keyboardFocus = el => {
    try {
        return el.matches(':focus-visible');
    } catch (e) {
        return true;
    }
};

// Whether a keydown is free for the dock to use. Space and Enter press a
// focused button or link, so they're left to it, except for a button in
// the dock that was clicked (a clicked tab keeps the focus, and Space
// should then start the tool rather than close its panel again).
// evenIfHandled: also when another of the dock's keys took it (the number
// keys both move the marker and sound the chord).
export function isShortcut(e, { evenIfHandled = false } = {}) {
    if ((e.defaultPrevented && !evenIfHandled) || e.ctrlKey || e.metaKey || e.altKey) return false;
    if (isField(e.target)) return false;
    if (document.querySelector('.ReactModal__Overlay')) return false;
    if (e.key === ' ' || e.key === 'Enter') {
        const control = e.target && e.target.closest && e.target.closest('button, a, [role="button"], summary');
        if (control && (!control.closest('.practice-dock') || keyboardFocus(control))) return false;
    }
    return true;
}

// "macOS" from userAgentData, "MacIntel" from the older navigator.platform.
const isMac = /mac|iphone|ipad/i.test((navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || '');
// The key that shows the hints, as KeyboardEvent.key and as written.
export const HINTS_KEY = isMac ? 'Meta' : 'Control';
export const HINTS_KEY_LABEL = isMac ? '⌘' : 'Ctrl';
// Hold it this long: a quick ⌘C shouldn't flash the hints.
const HINTS_AFTER_MS = 250;

// Whether the hints are showing: while the key is held on its own.
export function useKeyHints() {
    const [showing, setShowing] = useState(false);
    useEffect(() => {
        let timer = null;
        const hide = () => {
            clearTimeout(timer);
            timer = null;
            setShowing(false);
        };
        const onKeyDown = e => {
            if (e.key === HINTS_KEY) {
                if (!e.repeat) {
                    clearTimeout(timer);
                    timer = setTimeout(() => setShowing(true), HINTS_AFTER_MS);
                }
            } else {
                hide(); // a shortcut of the browser's or the system's
            }
        };
        const onKeyUp = e => {
            if (e.key === HINTS_KEY) hide();
        };
        // Switching apps with ⌘Tab never lets the key go here.
        const onVisibility = () => document.hidden && hide();
        window.addEventListener('keydown', onKeyDown, true);
        window.addEventListener('keyup', onKeyUp, true);
        window.addEventListener('blur', hide);
        window.addEventListener('mousedown', hide, true);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('keydown', onKeyDown, true);
            window.removeEventListener('keyup', onKeyUp, true);
            window.removeEventListener('blur', hide);
            window.removeEventListener('mousedown', hide, true);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, []);
    return showing;
}

// The key for a button, shown on it while the hints are (the dock gets the
// practice-dock--keys class). The button must be positioned.
export const KeyHint = ({ k }) => <kbd className="practice-key-hint" aria-hidden="true">{k}</kbd>;

export const spaceLabel = () => gettext('Space');

const Keys = ({ keys }) => (
    <span className="practice-shortcut-keys">
        {keys.map((key, i) => <kbd key={i}>{key}</kbd>)}
    </span>
);

const Row = ({ keys, children }) => (
    <li>
        <Keys keys={keys} />
        <span>{children}</span>
    </li>
);

// The panel listing them, opened with "?". tools: the dock's tools, for the
// ones this player has.
export function ShortcutsHelp({ tools }) {
    const has = id => tools.some(tool => tool.id === id);
    return (
        <div className="practice-shortcuts practice-tool">
            <ul className="practice-shortcuts-list">
                {tools.filter(tool => TOOL_KEYS[tool.id]).map(tool => (
                    <Row key={tool.id} keys={[TOOL_KEYS[tool.id].toUpperCase()]}>{tool.label}</Row>
                ))}
                <Row keys={['Esc']}>{gettext('Close the panel')}</Row>
                <Row keys={[HINTS_KEY_LABEL]}>{gettext('Hold to see the keys on the buttons')}</Row>
                <Row keys={['?']}>{gettext('These shortcuts')}</Row>
            </ul>
            <h3 className="practice-log-heading">{gettext('Playing')}</h3>
            <ul className="practice-shortcuts-list">
                <Row keys={[gettext('Space')]}>{gettext('Start or stop what the open panel plays')}</Row>
                <Row keys={['S']}>{gettext('Stop everything')}</Row>
                <Row keys={['+', '−']}>{gettext('Faster or slower')}</Row>
                <Row keys={['Enter']}>{gettext('Tap the tempo')}</Row>
            </ul>
            {has('chords') && <>
                <h3 className="practice-log-heading">{gettext('Chords')}</h3>
                <ul className="practice-shortcuts-list">
                    <Row keys={['1–9']}>{gettext("Hear one of the song's chords, numbered in the chords panel (while it's open)")}</Row>
                    <Row keys={['N']}>{gettext("Hear the chord you'll play next (the first one before you start)")}</Row>
                    <Row keys={['D']}>{gettext("Drone on the song's key, on or off")}</Row>
                </ul>
                <h3 className="practice-log-heading">{gettext('Following by tapping')}</h3>
                <ul className="practice-shortcuts-list">
                    <Row keys={['←', '→']}>{gettext('Previous or next chord (a page-turner pedal works too)')}</Row>
                    <Row keys={['1–9']}>{gettext("Jump to one of the song's chords")}</Row>
                </ul>
            </>}
            {has('changes') && <>
                <h3 className="practice-log-heading">{gettext('Chord changes')}</h3>
                <ul className="practice-shortcuts-list">
                    <Row keys={[gettext('Space')]}>{gettext('Start a round, then count a change')}</Row>
                    <Row keys={['1', '2']}>{gettext('Hear the two chords')}</Row>
                </ul>
            </>}
        </div>
    );
}
